import React, { useState, useEffect, useRef } from 'react'
import { Select, CompanyEditForm } from '../'
import { useDebounce, useModalDialog } from '../../hooks'
import { companyService } from '../../services'

const toOption = (c) => ({
  value: c.company_uuid,
  label: c.company_name,
  secondaryLabel: c.company_industry || undefined,
})

// Two effects/callbacks below can each prepend the same company (the
// "resolve preselected uuid" effect and a freshly-created company's
// onSaved/onChange) — dedupe so <Select> never gets two options with the
// same key.
const prependOption = (prev, item) => prev.some(o => o.value === item.value) ? prev : [item, ...prev]

// mode="uuid" (default): value/onChange carry a network.company company_uuid.
// mode="name": value/onChange carry a free-text string (kayenta_stories'
// speaker.company JSONB field stays a plain string — see plan A5.4). The
// picker is then just a suggestion list; nothing is written to network.company
// from here; that link is made via Company.getOrCreate wherever the string is
// resolved server-side (e.g. a future personSync.js). Not exercised by PR1-3.
//
// allowCreate adds an explicit "+ Skapa ... som nytt företag" (uuid mode) /
// "Fortsätt med fritext ..." (name mode) row at the bottom of the dropdown —
// this is a deliberate action, not something that happens implicitly on
// blur. uuid mode's create opens the same "Nytt företag" confirm modal as
// the Nätverk company page (shared CompanyEditForm); name mode's create is
// just free text, nothing is written to network.company.
export const CompanyPicker = ({
  name,
  value,
  onChange,
  label,
  placeholder = 'Sök företag...',
  mode = 'uuid',
  allowCreate = false,
  isNullable = false,
  disabled = false,
}) => {
  const { showModalDialog, hideModalDialog, setLoadingModalDialog } = useModalDialog()
  const dialogRef = useRef({ submit: () => Promise.resolve() })

  const [searchTerm, setSearchTerm] = useState('')
  const [options, setOptions] = useState([])
  const [resultCount, setResultCount] = useState(0)
  const [loading, setLoading] = useState(false)

  const { debounce } = useDebounce((term) => runSearch(term), 250, [])

  const runSearch = (term) => {
    setLoading(true)
    companyService.search(term)
      .then(res => {
        setOptions((res.companies || []).map(toOption))
        setResultCount(res.resultCount || (res.companies || []).length)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => { runSearch('') }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve a preselected uuid to a label even if it's outside the current search results
  useEffect(() => {
    if (mode === 'uuid' && value && !options.find(o => o.value === value)) {
      companyService.getByUuid(value).then(res => {
        if (res.company) setOptions(prev => prependOption(prev, toOption(res.company)))
      }).catch(() => {})
    } else if (mode === 'name' && value && !options.some(o => o.value === value)) {
      // mode="name"'s value/onChange are the free-text string itself (see the
      // header comment), but the shared <Select>'s display logic always
      // resolves the selected label by finding `options.find(o => o.value ===
      // value)` — and every option here is keyed by company_uuid (from
      // toOption), never by name. So a picked/typed name never matches any
      // option and would render blank without this: add a synthetic
      // {value: name, label: name} option so the lookup succeeds.
      setOptions(prev => prependOption(prev, { value, label: value }))
    }
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (term) => {
    setSearchTerm(term)
    debounce(term)
  }

  const openCreateDialog = (term) => {
    showModalDialog({
      title: 'Nytt företag',
      Content: () => (
        <CompanyEditForm
          initialName={term}
          dialogRef={dialogRef}
          hideModalDialog={hideModalDialog}
          setLoadingModalDialog={setLoadingModalDialog}
          onSaved={(company) => {
            setOptions(prev => prependOption(prev, toOption(company)))
            onChange({ name, value: company.company_uuid, company })
          }}
        />
      ),
      submitLabel: 'Skapa',
      submitLabelLoading: 'Skapar...',
      cancelLabel: 'Avbryt',
      onSubmit: () => dialogRef.current.submit(),
      onCancel: hideModalDialog,
    })
  }

  const handleChange = ({ value: val }) => {
    if (val === '__create__') {
      if (mode === 'name') {
        onChange({ name, value: searchTerm, company: null })
        return
      }
      openCreateDialog(searchTerm)
      return
    }

    const company = options.find(o => o.value === val)

    if (mode === 'name') {
      onChange({ name, value: company ? company.label : '', company: company ? { company_uuid: company.value, company_name: company.label } : null })
      return
    }

    onChange({ name, value: val || '', company: company ? { company_uuid: company.value, company_name: company.label } : null })
  }

  const hasExactMatch = options.some(o => o.label.toLowerCase().trim() === searchTerm.toLowerCase().trim())
  const createOption = allowCreate && searchTerm && !hasExactMatch
    ? [{ value: '__create__', label: mode === 'name' ? `Fortsätt med fritext "${searchTerm}"` : `+ Skapa "${searchTerm}" som nytt företag` }]
    : []

  return (
    <Select
      name={name}
      label={label}
      value={value || ''}
      options={[...options, ...createOption]}
      searchTerm={searchTerm}
      loading={loading}
      resultCount={resultCount}
      onInputChange={handleInputChange}
      onChange={handleChange}
      isNullable={isNullable}
      isClearable={mode === 'name'}
      disabled={disabled}
      placeholder={placeholder}
    />
  )
}
